import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Anchor, Button, Center, Group, Loader, Modal, Stack, Switch, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { benchmarkApi, type Benchmark } from '../../api';
import { formatAmount } from '../../format';
import { useLazyCount } from '../../ui/PageScroll';
import { BenchmarkFilterBar } from './BenchmarkFilterBar';
import { BenchmarkObjects } from './BenchmarkObjects';
import { type BenchmarkFilter, NO_FILTER, closeness, matches, normalUnit } from './benchmarkFilter';

/** Whether a catalog entry fits a cost group: same group, or one above or below it. */
export function fitsGroup(entry: string | null, group: string): boolean {
  if (!entry) return true;
  const a = entry.split('.')[0]!;
  const g = group.split('.')[0]!;
  const trim = (c: string): string => c.replace(/0+$/, '');
  return trim(a).startsWith(trim(g)) || trim(g).startsWith(trim(a));
}

/**
 * Choosing a rate from the catalog for one line: click von, Mittel or bis. The
 * line keeps where it came from in its source, so the figure stays traceable.
 */
export function BenchmarkPicker({ costGroup, unit = null, onClose, onPick }: {
  costGroup: string;
  /** The line's unit: entries in it come first, others are marked. */
  unit?: string | null;
  onClose: () => void;
  onPick: (b: Benchmark, value: number, which: 'von' | 'Mittel' | 'bis') => void;
}): JSX.Element {
  const list = useQuery({ queryKey: ['benchmarks'], queryFn: benchmarkApi.list });
  const [filter, setFilter] = useState<BenchmarkFilter>(NO_FILTER);
  const [onlyGroup, setOnlyGroup] = useState(true);
  const inGroup = (list.data ?? []).filter((b) => !onlyGroup || fitsGroup(b.costGroup, costGroup));
  const shown = inGroup.filter((b) => matches(b, filter)).sort((a, b) => closeness(a, costGroup, unit) - closeness(b, costGroup, unit) || (a.costGroup ?? '').localeCompare(b.costGroup ?? ''));
  const lazy = useLazyCount(shown.length, 150, `${JSON.stringify(filter)}|${String(onlyGroup)}`);
  const cell = (b: Benchmark, v: number | null, which: 'von' | 'Mittel' | 'bis'): JSX.Element => (
    <Table.Td ta="right">
      {v === null ? <Text size="sm" c="dimmed">—</Text> : <Button size="compact-xs" variant={which === 'Mittel' ? 'light' : 'subtle'} onClick={() => onPick(b, v, which)}>{formatAmount(v)}</Button>}
    </Table.Td>
  );

  return (
    <Modal opened onClose={onClose} title={`Kennwert aus dem Katalog für KG ${costGroup}`} size="90%">
      <Stack gap="sm">
        <Group gap="sm">
          <div style={{ flex: 1 }}><BenchmarkFilterBar list={inGroup} filter={filter} onChange={setFilter} groupLocked={onlyGroup} /></div>
          <Switch size="xs" label={`nur KG ${costGroup} und verwandte`} checked={onlyGroup} onChange={(e) => setOnlyGroup(e.currentTarget.checked)} />
        </Group>
        {filter.buildingType && <BenchmarkObjects key={filter.buildingType} buildingType={filter.buildingType} />}
        {list.isPending && <Center h={80}><Loader size="sm" /></Center>}
        {list.isError && <Alert color="red">Der Katalog konnte nicht geladen werden.</Alert>}
        {list.data && shown.length === 0 && <Text size="sm" c="dimmed">Keine passenden Kennwerte. Kennwerte werden unter „Verwaltung › Bibliotheken › Kosten-Kennwerte“ eingepflegt.</Text>}
        {shown.length > 0 && (
          <Table.ScrollContainer minWidth={760} maxHeight={460}>
            <Table verticalSpacing={2} className="pst-tabular" highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={70}>KG</Table.Th>
                  <Table.Th>Bezeichnung</Table.Th>
                  <Table.Th w={160}>Quelle</Table.Th>
                  <Table.Th w={70}>Einheit</Table.Th>
                  <Table.Th w={90} ta="right">von</Table.Th>
                  <Table.Th w={90} ta="right">Mittel</Table.Th>
                  <Table.Th w={90} ta="right">bis</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.slice(0, lazy.shown).map((b) => (
                  <Table.Tr key={b.id}>
                    <Table.Td><Text size="sm" ff="monospace">{b.costGroup}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">{b.label}</Text>
                      {b.description && <Text size="xs" c="dimmed" lineClamp={2} title={b.description} style={{ whiteSpace: 'pre-line' }}>{b.description}{b.duration ? ` · ${b.duration}` : ''}</Text>}
                      {b.buildingType && <Text size="xs" c="dimmed">{b.buildingType}{filter.buildingType !== b.buildingType && <> · <Anchor size="xs" component="button" type="button" onClick={() => setFilter({ ...filter, buildingType: b.buildingType })}>Beispielobjekte</Anchor></>}</Text>}
                    </Table.Td>
                    <Table.Td><Text size="xs">{[b.source, b.edition].filter(Boolean).join(' ')}</Text><Text size="xs" c="dimmed">{[b.priceDate, b.region].filter(Boolean).join(', ')}</Text></Table.Td>
                    <Table.Td><Text size="sm" c={unit && normalUnit(b.unit) !== normalUnit(unit) ? 'orange.7' : undefined} title={unit && normalUnit(b.unit) !== normalUnit(unit) ? `andere Einheit als die Position (${unit})` : undefined}>{b.unit}</Text></Table.Td>
                    {cell(b, b.low, 'von')}
                    {cell(b, b.mid, 'Mittel')}
                    {cell(b, b.high, 'bis')}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {lazy.more}
          </Table.ScrollContainer>
        )}
      </Stack>
    </Modal>
  );
}
